import { useEffect, useState } from "react";
import { AuthTokenRetrieve, LoginButton } from "../Utility/AuthTokenRetrieve";
import { apiFetch } from "../Utility/api";
import "../Food/RecipeList.css";
import "./FoodDiary.css";
import { defaultBowelEventForm, fetchBowelEvents } from "./FoodDiaryShared";

const FoodDiaryBowelEvents = () => {
  const [shouldShow, setShouldShow] = useState();
  const [events, setEvents] = useState([]);
  const [form, setForm] = useState(defaultBowelEventForm());
  const [editingId, setEditingId] = useState(null);
  const [statusMessage, setStatusMessage] = useState("");
  const { shouldShow: shouldShowGoogle } = AuthTokenRetrieve();

  useEffect(() => {
    setShouldShow(shouldShowGoogle);
  }, [shouldShowGoogle]);

  useEffect(() => {
    if (!shouldShow) {
      return;
    }
    fetchBowelEvents().then((data) => setEvents(data || []));
  }, [shouldShow]);

  const refreshEvents = () => {
    fetchBowelEvents().then((data) => setEvents(data || []));
  };

  const handleChange = (event) => {
    const { name, value, type, checked } = event.target;
    setForm((currentValue) => ({
      ...currentValue,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    const requestUrl = editingId
      ? `/food/diary/bowel-events/${editingId}`
      : "/food/diary/bowel-events";

    apiFetch(requestUrl, {
      method: editingId ? "PUT" : "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form),
    })
      .then((response) => response.json())
      .then(() => {
        setStatusMessage(editingId ? "Bowel event updated." : "Bowel event logged.");
        setForm(defaultBowelEventForm());
        setEditingId(null);
        refreshEvents();
      });
  };

  const beginEdit = (entry) => {
    const [datePart, timePart, meridiem] = entry.occurredAt.split(" ");
    const [month, day, year] = datePart.split("/");
    let [hours, minutes] = timePart.split(":");
    let parsedHours = Number(hours);
    if (meridiem === "PM" && parsedHours < 12) parsedHours += 12;
    if (meridiem === "AM" && parsedHours === 12) parsedHours = 0;

    setForm({
      occurredAt: `${year}-${month}-${day}T${String(parsedHours).padStart(2, "0")}:${minutes}`,
      stoolFormScale: String(entry.stoolFormScale ?? ""),
      urgency: String(entry.urgency ?? ""),
      painSeverity: String(entry.painSeverity ?? ""),
      bloodPresent: !!entry.bloodPresent,
      mucusPresent: !!entry.mucusPresent,
      notes: entry.notes || "",
    });
    setEditingId(entry.id);
    setStatusMessage("");
  };

  const deleteEntry = (entryId) => {
    apiFetch(`/food/diary/bowel-events/${entryId}`, {
      method: "DELETE",
    })
      .then((response) => response.json())
      .then(() => {
        setStatusMessage("Bowel event deleted.");
        setEditingId(null);
        setForm(defaultBowelEventForm());
        refreshEvents();
      });
  };

  if (!shouldShow) {
    return (
      <div className="searchBarDiv formViewWrapper FoodDiaryLogin">
        <h2>Food diary</h2>
        <p>Sign in to track bowel events.</p>
        <LoginButton setShouldShow={setShouldShow}></LoginButton>
      </div>
    );
  }

  return (
    <div className="FoodDiaryPage">
      <div className="FoodDiaryGrid">
        <div className="searchBarDiv formViewWrapper FoodDiaryPanel FoodDiarySinglePanel">
          <h2 className="FoodDiaryTitle">Bowel events</h2>
          <p className="FoodDiarySubtitle">Track stool form, urgency, pain, blood, and mucus.</p>
          {statusMessage ? <p className="FoodDiaryStatus">{statusMessage}</p> : null}
          <form onSubmit={handleSubmit}>
            <div className="FoodDiaryTwoColumn">
              <div className="inputDiv">
                <label>Occurred</label>
                <input type="datetime-local" className="inputBox addInput" name="occurredAt" value={form.occurredAt} onChange={handleChange} />
              </div>
              <div className="inputDiv">
                <label>Stool form (1-7)</label>
                <input className="inputBox addInput" name="stoolFormScale" value={form.stoolFormScale} onChange={handleChange} />
              </div>
            </div>
            <div className="FoodDiaryTwoColumn">
              <div className="inputDiv">
                <label>Urgency (1-10)</label>
                <input className="inputBox addInput" name="urgency" value={form.urgency} onChange={handleChange} />
              </div>
              <div className="inputDiv">
                <label>Pain (1-10)</label>
                <input className="inputBox addInput" name="painSeverity" value={form.painSeverity} onChange={handleChange} />
              </div>
            </div>
            <div className="FoodDiaryCheckRow">
              <label><input type="checkbox" name="bloodPresent" checked={form.bloodPresent} onChange={handleChange} /> Blood present</label>
              <label><input type="checkbox" name="mucusPresent" checked={form.mucusPresent} onChange={handleChange} /> Mucus present</label>
            </div>
            <div className="inputDiv">
              <label>Notes</label>
              <textarea className="inputBox textAreaAdd FoodDiaryTextarea" name="notes" value={form.notes} onChange={handleChange} />
            </div>
            <div className="FoodDiaryActionRow">
              <button type="submit" className="summaryButton">{editingId ? "Save Event" : "Add Event"}</button>
              {editingId ? <button type="button" className="summaryButton" onClick={() => { setEditingId(null); setForm(defaultBowelEventForm()); }}>Cancel</button> : null}
            </div>
          </form>
        </div>

        <div className="shoppingListDiv FoodDiaryPanel">
          <h2 className="FoodDiaryTitle">Recent bowel events</h2>
          {events.length ? events.map((entry) => (
            <div className="FoodDiaryHistoryCard" key={entry.id}>
              <div className="FoodDiaryMealItemHeader">
                <strong>{entry.occurredAt}</strong>
                <span>Form {entry.stoolFormScale ?? "-"}</span>
              </div>
              <div className="FoodDiaryHistoryMeta">
                <span>Urgency {entry.urgency ?? "-"}</span>
                <span>Pain {entry.painSeverity ?? "-"}</span>
                <span>{entry.bloodPresent ? "Blood" : "No blood"}</span>
                <span>{entry.mucusPresent ? "Mucus" : "No mucus"}</span>
              </div>
              {entry.notes ? <p className="FoodDiaryMealNotes">{entry.notes}</p> : null}
              <div className="FoodDiaryActionRow">
                <button className="summaryButton" onClick={() => beginEdit(entry)}>Edit</button>
                <button className="summaryButton" onClick={() => deleteEntry(entry.id)}>Delete</button>
              </div>
            </div>
          )) : <p>No bowel events logged yet.</p>}
        </div>
      </div>
    </div>
  );
};

export default FoodDiaryBowelEvents;
